import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Wallet, AlertCircle, Package, Plus, Boxes, BookOpen, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import { dashboardApi } from '../../services/api';
import { formatCurrency, StatusBadge, PaymentBadge } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { QuickAccessActions } from '../../components/QuickAccessMenu';
import { inventoryApi } from '../../services/api';
import OnboardingModal from '../../components/OnboardingModal';

function MetricCard({ title, value, icon: Icon, color, subtitle, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
      <div className={`w-11 h-11 ${color} rounded-xl flex items-center justify-center mb-4`}>
        <Icon size={20} className="text-white" />
      </div>
      <p className="text-sm text-slate-500 mb-1">{title}</p>
      {loading ? <div className="h-8 bg-slate-100 rounded-lg animate-pulse w-32" /> :
        <p className="text-2xl font-extrabold text-slate-900 font-manrope tracking-tight">{value}</p>}
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const { lang, t } = useLanguage();
  const { user, store } = useAuth();
  const navigate = useNavigate();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardApi.getSummary().then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: chartData = [] } = useQuery({
    queryKey: ['dashboard-chart'],
    queryFn: () => dashboardApi.getSalesChart().then(r => r.data),
  });
  const { data: inventory } = useQuery({ queryKey: ['inventory', 'summary'], queryFn: () => inventoryApi.summary().then(result => result.data), enabled: Boolean(store?.inventory_enabled) });
  const { data: onboarding } = useQuery({ queryKey: ['dashboard-onboarding'], queryFn: () => dashboardApi.getOnboarding().then(result => result.data), enabled: user?.role === 'owner', refetchOnWindowFocus: true });

  const metrics = [
    { title: t('Pendapatan Hari Ini', 'Today Revenue'), value: formatCurrency(summary?.daily_revenue || 0), icon: TrendingUp, color: 'bg-[#0f6c44]', subtitle: t(`${summary?.new_orders_today || 0} pesanan baru`, `${summary?.new_orders_today || 0} new orders`) },
    { title: t('Kas Diterima', 'Cash Received'), value: formatCurrency(summary?.cash_received || 0), icon: Wallet, color: 'bg-blue-500', subtitle: t('Tunai hari ini', 'Cash today') },
    { title: t('Piutang', 'Receivables'), value: formatCurrency(summary?.outstanding_receivables || 0), icon: AlertCircle, color: 'bg-orange-500', subtitle: t('Belum lunas', 'Outstanding') },
    { title: t('Pesanan Aktif', 'Active Orders'), value: String(summary?.active_orders || 0), icon: Package, color: 'bg-purple-500', subtitle: t(`${summary?.total_customers || 0} pelanggan`, `${summary?.total_customers || 0} customers`) },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">Dashboard</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Selamat datang', 'Welcome')}, {user?.name}</p>
          </div>
          <button onClick={() => navigate('/admin/order-create')} data-testid="create-order-button"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            <Plus size={18} />{t('Buat Pesanan', 'New Order')}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_290px]">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{metrics.map((m, i) => <MetricCard key={i} {...m} loading={isLoading} />)}</div>
          {user?.role === 'owner' && <section data-testid="dashboard-tutorial-center-card" className="flex flex-col justify-between rounded-2xl border border-emerald-100 bg-emerald-50 p-5"><div><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0f6c44] text-white"><BookOpen size={19}/></span><h2 className="mt-4 font-manrope text-lg font-bold text-slate-900">Pusat Tutorial</h2><p className="mt-1 text-sm leading-relaxed text-slate-600">Pelajari pengaturan, transaksi, CRM, dan laporan KasirWA.</p></div><button onClick={()=>navigate('/admin/tutorials')} data-testid="open-tutorial-center-button" className="mt-5 flex items-center justify-between rounded-xl bg-white px-3.5 py-2.5 text-sm font-bold text-[#0f6c44] shadow-sm transition-colors hover:bg-emerald-100">Lihat Semua Tutorial <ArrowRight size={16}/></button></section>}
        </div>

        {store?.inventory_enabled && (inventory?.low_stock_count > 0 || inventory?.out_of_stock_count > 0) && <button onClick={() => navigate('/admin/inventory')} data-testid="dashboard-low-stock-card" className="flex w-full items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-amber-100"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><Boxes size={19}/></span><span><span className="block text-sm font-bold text-amber-900">Stok perlu diperhatikan</span><span className="mt-0.5 block text-xs text-amber-700">{inventory.out_of_stock_count} habis · {inventory.low_stock_count} menipis — buka Inventory</span></span></button>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
            <h3 className="font-semibold text-slate-800 mb-5 font-manrope">{t('Pendapatan 7 Hari Terakhir', 'Last 7 Days Revenue')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip formatter={v => [formatCurrency(v), t('Pendapatan', 'Revenue')]} contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                <Bar dataKey="revenue" fill="#0f6c44" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
            <h3 className="mb-4 font-manrope font-semibold text-slate-800">Akses Cepat</h3>
            <QuickAccessActions userRole={user?.role} compact />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between p-6 border-b border-slate-50">
            <h3 className="font-semibold text-slate-800 font-manrope">{t('Pesanan Terbaru', 'Recent Orders')}</h3>
            <button onClick={() => navigate('/admin/orders')} className="text-sm text-[#0f6c44] font-medium hover:underline">
              {t('Lihat semua', 'See all')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-50">
                  {[t('Kode', 'Code'), t('Pelanggan', 'Customer'), t('Total', 'Total'), t('Bayar', 'Payment'), t('Status', 'Status')].map(h => (
                    <th key={h} className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? [1,2,3].map(i => (
                  <tr key={i}>{[1,2,3,4,5].map(j => <td key={j} className="px-6 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>
                )) : (summary?.recent_orders?.length === 0) ? (
                  <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-400 text-sm">{t('Belum ada pesanan', 'No orders yet')}</td></tr>
                ) : (summary?.recent_orders || []).map(order => (
                  <tr key={order.id} onClick={() => navigate(`/admin/orders/${order.id}`)}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer transition-colors">
                    <td className="px-6 py-4"><span className="font-mono text-xs font-bold text-[#0f6c44] bg-emerald-50 px-2 py-0.5 rounded">{order.tracking_code}</span></td>
                    <td className="px-6 py-4 text-sm text-slate-700 font-medium">{order.customer_name || '-'}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-800">{formatCurrency(order.total_amount)}</td>
                    <td className="px-6 py-4"><PaymentBadge status={order.payment_status} lang={lang} /></td>
                    <td className="px-6 py-4"><StatusBadge status={order.status} lang={lang} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {!onboardingDismissed && <OnboardingModal onboarding={onboarding} onNavigate={navigate} onCompleted={() => setOnboardingDismissed(true)} />}
      </div>
    </AdminLayout>
  );
}
